/**
 * Custom hook for pending surveys table columns
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { ProcessStepAssignment, Survey } from '@/api/queries/useTasks';
import type { AssignmentProcess, AssignmentProcessStepRecording } from '@bliks-io/contracts';
import { useStandardRecordingTimeMs } from '@/hooks/useStandardRecordingTime';
import {
  Button,
  renderFallback,
  createColumnHeader,
  createDueDateColumn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { ProcessStepCell } from '../components/ProcessStepCell';
import type { ProcessStepInfo } from '../types';

type StepRecording = Exclude<Survey['stepRecording'], string>;

const toStepInfo = (recording: StepRecording): ProcessStepInfo => ({
  actionType: recording.actionType,
  task: recording.task,
  generatedName: recording.generatedName,
  generatedDescription: recording.generatedDescription
});

interface UsePendingSurveysColumnsParams {
  slug: string;
  getProcessName: (process: string | AssignmentProcess) => string;
  getDepartment: (process: string | AssignmentProcess) => string;
  getProcessStepInfo: (
    // Mirrors ProcessStepCell's prop: the source union includes the populated
    // start event node arm since issue 11.
    step:
      | string
      | ProcessStepInfo
      | AssignmentProcessStepRecording
      | 'START'
      | ProcessStepAssignment['sourceProcessStep'],
    ruleIds?: string[]
  ) => {
    label: string;
    description?: string;
    actionType?: 'task' | 'decision' | 'start';
    eventType?: string;
  };
  onSurveyClick: (surveyId: string) => void;
}

export function usePendingSurveysColumns(params: UsePendingSurveysColumnsParams) {
  const { t } = useTranslation();
  const { slug, getProcessName, getDepartment, getProcessStepInfo, onSurveyClick } = params;
  const standardRecordingTimeMs = useStandardRecordingTimeMs();

  return useMemo<DataTableColumnDef<Survey>[]>(
    () => [
      {
        accessorKey: 'task',
        meta: { label: t('myTasks.processStep'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.processStep')),
        cell: ({ row }) => {
          const survey = row.original;
          if (!survey.stepRecording || typeof survey.stepRecording === 'string')
            return renderFallback(null);
          return (
            <ProcessStepCell
              step={toStepInfo(survey.stepRecording)}
              getStepInfo={getProcessStepInfo}
            />
          );
        },
        accessorFn: (row) => {
          if (!row.stepRecording || typeof row.stepRecording === 'string') return '';
          return getProcessStepInfo(toStepInfo(row.stepRecording)).label;
        },
        enableColumnFilter: true
      },
      {
        accessorKey: 'process',
        id: 'processName',
        meta: { label: t('myTasks.process'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.process')),
        cell: ({ row }) => {
          const survey = row.original;
          if (!survey.stepRecording || typeof survey.stepRecording === 'string')
            return renderFallback(null);
          const process = survey.stepRecording.process;
          const processId = typeof process === 'string' ? process : process._id;

          return (
            <Link
              to="/$slug/processes/all-processes/$processId"
              params={{ slug, processId }}
              className="font-medium hover:underline"
            >
              {getProcessName(process)}
            </Link>
          );
        },
        accessorFn: (row) => {
          if (!row.stepRecording || typeof row.stepRecording === 'string') return '';
          return getProcessName(row.stepRecording.process);
        },
        enableColumnFilter: true
      },
      {
        id: 'department',
        meta: { label: t('myTasks.department'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.department')),
        cell: ({ row }) => {
          const survey = row.original;
          if (!survey.stepRecording || typeof survey.stepRecording === 'string')
            return renderFallback(null);
          return getDepartment(survey.stepRecording.process);
        },
        accessorFn: (row) => {
          if (!row.stepRecording || typeof row.stepRecording === 'string') return '';
          return getDepartment(row.stepRecording.process);
        },
        enableColumnFilter: true
      },
      createDueDateColumn<Survey>(t('myTasks.dueDate'), standardRecordingTimeMs),
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button size="small" variant="primary" onClick={() => onSurveyClick(row.original._id)}>
              {t('myTasks.completeSurvey', { defaultValue: 'Complete Survey' })}
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [
      t,
      slug,
      getProcessStepInfo,
      getProcessName,
      getDepartment,
      onSurveyClick,
      standardRecordingTimeMs
    ]
  );
}
